import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import {
  type DeviceDetails,
  type DeviceListItem,
  useDevicesStore,
} from "@/_store/devices.module.ts";
import {
  type MspDeviceDetails,
  type MspDeviceListItem,
  useMspDevicesStore,
} from "@/_store/msp/devices/msp-devices.module.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import type { PrivacySensitiveDataSettings } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module.ts";
import { usePrivacySensitiveDataStore } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module.ts";
import type { ISelection } from "@/_store/selector.module.ts";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";

// Helpers & Constants
import { AppContext } from "@/types.ts";
import { ActionTypeTarget, DeviceAction } from "@/constants/devices.ts";
import { ModalWidth } from "@/constants/modals.ts";
import { RouteName } from "@/constants/routes.ts";
import { TicketTrigger } from "@/constants/tickets.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { isMspAccessRestricted, isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { isGlobalActionRestricted } from "@/_helpers/global-permissions.ts";
import { i18n } from "@/plugins/i18n.ts";

// Modals
import DlpScanModal from "@/components/modals/DlpScanModal.vue";
import BulkTicketCloseModal from "@/components/modals/BulkTicketCloseModal.vue";
import AddOrRemoveDeviceLabelModal from "@/components/modals/AddOrRemoveDeviceLabelModal.vue";
import SendMessageToAgentModal from "@/components/modals/SendMessageToAgentModal.vue";
import RemoteShellModal from "@/components/RemoteShellModal.vue";
import MspRemoteShellModal from "@/components/MspRemoteShellModal.vue";

import type {
  DevicePreviewActionPayload,
  DevicePreviewActionItem,
  DevicePreviewMspActionItem,
  DevicePreviewWorkspaceActionItem,
  MspDevicePreviewConfig,
  SingleWorkspaceDevicePreviewConfig,
} from "./device-preview.types.ts";
import router from "@/_helpers/router.ts";
import { isModuleDisabled } from "@/_helpers/subscription";
import { isAccessRestricted, isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import {
  GlobalRoleScopes,
  GlobalRoleScopeSection,
  RolePermissionsScope,
  WorkspaceManagementScopeSections,
} from "@/constants/roles.ts";

const DEFAULT_DEVICE_SETTINGS: PrivacySensitiveDataSettings = {
  monitorNonPublicData: false,
  monitorPersonalData: false,
  monitorHealthData: false,
  monitorCreditCardData: false,
  wizardParameters: {
    industries: [],
    isDefault: false,
  },
};

const TICKET_MODULES = [
  SubscriptionModule.EDR,
  SubscriptionModule.ENDPOINT_SECURITY,
  SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
] as const;

function createDlpSettingsHandlers<TDevice>({
  fetchSettings,
  shouldSkipFetch,
}: {
  fetchSettings: (device: TDevice) => Promise<PrivacySensitiveDataSettings | undefined>;
  shouldSkipFetch: (device: TDevice) => boolean;
}) {
  const deviceSettings = ref<PrivacySensitiveDataSettings>({ ...DEFAULT_DEVICE_SETTINGS });
  const deviceSettingsDisabled = ref(true);

  const applyDeviceSettings = (
    settings: PrivacySensitiveDataSettings | undefined = DEFAULT_DEVICE_SETTINGS
  ) => {
    const normalizedSettings = { ...DEFAULT_DEVICE_SETTINGS, ...settings };
    deviceSettings.value = normalizedSettings;
    const monitoringOptions = [
      normalizedSettings.monitorNonPublicData,
      normalizedSettings.monitorPersonalData,
      normalizedSettings.monitorHealthData,
      normalizedSettings.monitorCreditCardData,
    ];

    deviceSettingsDisabled.value = monitoringOptions.every((option) => !option);
  };

  const fetchDeviceSettings = async (device: TDevice) => {
    if (shouldSkipFetch(device)) {
      applyDeviceSettings();
      return;
    }
    try {
      const data = await fetchSettings(device);
      applyDeviceSettings(data);
    } catch {
      applyDeviceSettings();
    }
  };

  return { deviceSettingsDisabled, fetchDeviceSettings };
}

// --- Permission Helper ---
function createPermissionRefs(context: AppContext) {
  const isMsp = context === AppContext.MSP;

  const actionNotAllowed = computed(() =>
    isMsp
      ? isMspActionRestricted(RolePermissionsScope.VIEWS, "devicesView")
      : isWorkspaceFrozenOrActionRestricted(RolePermissionsScope.VIEWS, "devicesView")
  );

  const accessChecker = isMsp ? isMspAccessRestricted : isAccessRestricted;

  const activityLogsAccessRestricted = computed(() =>
    accessChecker(
      RolePermissionsScope.WORKSPACE_MANAGEMENT,
      WorkspaceManagementScopeSections.ACTIVITY_LOGS
    )
  );

  const restrictedTicketModules = computed(() =>
    TICKET_MODULES.filter((module) => accessChecker(RolePermissionsScope.TICKETS, module))
  );

  const ticketsAccessRestricted = computed(
    () => restrictedTicketModules.value.length === TICKET_MODULES.length
  );

  const modulesTooltipText = computed(() =>
    i18n.global.t(
      "entityTicketsList.modulesTooltip",
      {
        modules: restrictedTicketModules.value
          .map((module) => i18n.global.t(`subscriptions.modules.${module}`))
          .join(", "),
      },
      restrictedTicketModules.value.length
    )
  );

  return {
    actionNotAllowed,
    activityLogsAccessRestricted,
    ticketsAccessRestricted,
    restrictedTicketModules,
    modulesTooltipText,
  };
}

type DeviceStore = ReturnType<typeof useDevicesStore> | ReturnType<typeof useMspDevicesStore>;

function createBaseConfig(
  store: DeviceStore,
  permissions: ReturnType<typeof createPermissionRefs>,
  context: AppContext
) {
  const { deviceDetailsLoading, deviceDrivesLoading, protectionLimit } = storeToRefs(store);
  return {
    context,
    deviceDetailsLoading,
    deviceDrivesLoading,
    protectionLimit,
    downloadLogs: (deviceId: string) => store.downloadLogs(deviceId),
    getDeviceAdvancedInfo: (enrollmentCode: string) => store.getDeviceAdvancedInfo(enrollmentCode),
    mergeDuplicateDevices: (enrollmentCode: string) => store.mergeDuplicateDevices(enrollmentCode),
    ignoreDuplicateDevicesMerge: (enrollmentCode: string) =>
      store.ignoreDuplicateDevicesMerge(enrollmentCode),
    undoActivityLog: (enrollmentCode: string, activityLogId: string) =>
      store.undoActivityLog(enrollmentCode, activityLogId),
    ...permissions,
  };
}

function isWorkspaceActionItem(
  item: DevicePreviewActionItem
): item is DevicePreviewWorkspaceActionItem {
  return "selection" in item;
}

function createActionHandler<
  D extends DeviceDetails | MspDeviceDetails,
  A extends DevicePreviewActionItem,
>(
  baseConfig: ReturnType<typeof createBaseConfig>,
  applyDeviceAction: (payload: DevicePreviewActionPayload<A>) => Promise<void>,
  buildActionItem: (action: DeviceAction, details: D) => A,

  // Configuration to handle differences
  options: {
    ticketsRoute: RouteName.TICKETS | RouteName.MSP_TICKETS_PAGE;
    remoteShellComponent: typeof RemoteShellModal | typeof MspRemoteShellModal;
    excludedActions?: Set<DeviceAction>;
  }
) {
  const dialogsStore = useDialogsStore();

  return async (action: DeviceAction, details: D, payload?: DeviceLabel) => {
    // Check if this action is excluded in the current context
    if (options.excludedActions?.has(action)) {
      return; // Stop here if the action is excluded
    }

    const baseItem = buildActionItem(action, details);

    switch (action) {
      case DeviceAction.REMOVE_LABEL:
        if (!payload?.id) break;
        await applyDeviceAction({
          action,
          item: { ...baseItem, metadata: { labels: [payload.id] } },
        });
        break;
      case DeviceAction.EXAMINE_MALWARE:
        await router.push({
          name: options.ticketsRoute,
          query: {
            eventTriggers: TicketTrigger.MALWARE_ON_ENDPOINT,
            search: details.enrollmentCode,
          },
        });
        break;
      case DeviceAction.VIEW_DETAILS:
        await router.push({
          name: options.ticketsRoute,
          query: {
            search: details.enrollmentCode,
            eventTriggers: (details.vulnerabilities || []).join(","),
          },
        });
        break;
      case DeviceAction.DOWNLOAD_LOGS:
        await baseConfig.downloadLogs(details.device.id);
        break;
      case DeviceAction.LICENSE:
        await applyDeviceAction({
          action,
          item: { ...baseItem },
        });
        break;
      case DeviceAction.SHOW_ADVANCED_INFORMATION:
        await baseConfig.getDeviceAdvancedInfo(details.enrollmentCode);
        break;
      case DeviceAction.COLLECT_QUARANTINE_DATA:
        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            action,
            item: { ...baseItem },
            callback: applyDeviceAction,
            disable: isGlobalActionRestricted(
              GlobalRoleScopes.GLOBAL_SCOPE,
              GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
              "collectQuarantineData"
            ),
          })
        );
        break;
      case DeviceAction.COLLECT_LOGS:
        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            action,
            item: { ...baseItem },
            callback: applyDeviceAction,
            disable: isGlobalActionRestricted(
              GlobalRoleScopes.GLOBAL_SCOPE,
              GlobalRoleScopeSection.SPECIAL_PERMISSIONS,
              "collectLogs"
            ),
          })
        );
        break;
      case DeviceAction.REMOVE_FROM_PROTECTION:
      case DeviceAction.ENABLE_FIREWALL:
      case DeviceAction.REMOTE_SCAN:
      case DeviceAction.ENFORCE_UAC:
      case DeviceAction.DISABLE_DEVELOPER_MODE:
      case DeviceAction.DISABLE_TAMPER_RESISTANCE:
      case DeviceAction.ALLOW_NO_ENCRYPTION:
      case DeviceAction.STOP_REMOTE_SCAN:
      case DeviceAction.DISABLE_NETWORK_BLOCK_MODE:
      case DeviceAction.ENABLE_NETWORK_BLOCK_MODE:
      case DeviceAction.SHUTDOWN_DEVICE:
      case DeviceAction.REBOOT_DEVICE:
      case DeviceAction.STOP_DLP_SCAN:
      case DeviceAction.UPDATE_AGENT_TO_LATEST:
      case DeviceAction.ENABLE_TAMPER_RESISTANCE:
      case DeviceAction.UNINSTALL_AGENT:
        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            action,
            item: { ...baseItem, deviceName: details.device.hostname },
            callback: applyDeviceAction,
            disable: baseConfig.actionNotAllowed.value,
          })
        );
        break;
      case DeviceAction.UNLICENSE:
        dialogsStore.openDialog({
          ...confirmationDialogsConfigConstructor({
            action,
            item: { ...baseItem, deviceName: details.device.hostname },
            callback: applyDeviceAction,
            disable: baseConfig.actionNotAllowed.value,
          }),
          header: {
            title: i18n.global.t(`modals.${action}.title`),
            close: true,
            titleClass: "text-red-dark",
            titleIcon: "warning",
          },
        });
        break;
      case DeviceAction.ENABLE_PROTECTION: {
        const limit = baseConfig.protectionLimit.value;
        if (limit && limit.remaining <= 0) {
          dialogsStore.openDialog({
            ...confirmationDialogsConfigConstructor({
              action,
              item: { ...baseItem },
              callback: applyDeviceAction,
              disable: baseConfig.actionNotAllowed.value,
            }),
            header: {
              title: i18n.global.t(`modals.${action}.title`),
              close: true,
              titleClass: "text-red-dark",
              titleIcon: "warning",
            },
          });
        } else {
          await applyDeviceAction({
            action,
            item: { ...baseItem },
          });
        }
        break;
      }
      case DeviceAction.RESET_MFA:
        const resetConfig = confirmationDialogsConfigConstructor({
          action,
          item: { ...baseItem },
          callback: applyDeviceAction,
          disable: baseConfig.actionNotAllowed.value,
        });
        dialogsStore.openDialog({
          ...resetConfig,
          header: {
            ...resetConfig.header,
            titleClass: "text-red-dark",
            titleIcon: "warning",
          },
        });
        break;
      case DeviceAction.DLP_PARTIAL_SCAN:
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            width: ModalWidth.SMALL,
            action,
            item: { ...baseItem, partialScan: true },
            component: DlpScanModal,
            callback: applyDeviceAction,
            disable: baseConfig.actionNotAllowed.value,
          })
        );
        break;
      case DeviceAction.MARK_AS_PROCESSED: {
        const markAsProcessedCallback = isWorkspaceActionItem(baseItem)
          ? ({ selection }: { selection: ISelection }) => {
              applyDeviceAction({
                action,
                item: { ...baseItem, selection } as A,
              });
            }
          : () => {
              applyDeviceAction({
                action,
                item: baseItem,
              });
            };

        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            item: {
              ...baseItem,
              callback: markAsProcessedCallback,
              modules: [
                SubscriptionModule.ENDPOINT_SECURITY,
                SubscriptionModule.ENDPOINT_DATA_GOVERNANCE,
                SubscriptionModule.EDR,
              ],
            },
            action,
            component: BulkTicketCloseModal,
            width: ModalWidth.MEDIUM,
            callback: () => {},
            hideFooter: true,
            disable: baseConfig.actionNotAllowed.value,
          })
        );
        break;
      }
      case DeviceAction.ADD_LABEL:
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            item: { ...baseItem },
            action,
            component: AddOrRemoveDeviceLabelModal,
            width: ModalWidth.SMALL,
            callback: applyDeviceAction,
            disable: baseConfig.actionNotAllowed.value,
          })
        );
        break;
      case DeviceAction.OPEN_REMOTE_SHELL_SESSION: {
        const modalBase = componentDialogsConfigConstructor({
          item: { ...details, ...baseItem },
          action,
          component: options.remoteShellComponent, // <-- Use parametrized component
          width: 900,
          callback: applyDeviceAction,
          disable: baseConfig.actionNotAllowed.value,
        });
        // Apply MSP-specific props if it's the MSP modal
        const dialogProps =
          options.remoteShellComponent === MspRemoteShellModal
            ? { ...modalBase, scrollable: true, footer: null }
            : { ...modalBase, footer: null };
        dialogsStore.openDialog(dialogProps);
        break;
      }
      case DeviceAction.SEND_MESSAGE_TO_AGENT:
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            item: { ...baseItem },
            action,
            component: SendMessageToAgentModal,
            width: ModalWidth.MEDIUM,
            callback: applyDeviceAction,
            disable: baseConfig.actionNotAllowed.value,
            customErrorHandling: true,
            showValidationAlert: true,
          })
        );
        break;
    }
  };
}

// --- Single Workspace Factory ---
export const getSingleWorkspaceDevicePreviewConfig = (): SingleWorkspaceDevicePreviewConfig => {
  const devicesStore = useDevicesStore();
  const { deviceDetails } = storeToRefs(devicesStore);
  const privacySensitiveDataStore = usePrivacySensitiveDataStore();
  const permissions = createPermissionRefs(AppContext.SINGLE_WORKSPACE);
  const baseConfig = createBaseConfig(devicesStore, permissions, AppContext.SINGLE_WORKSPACE);
  const { deviceSettingsDisabled, fetchDeviceSettings } = createDlpSettingsHandlers<DeviceListItem>(
    {
      shouldSkipFetch: () => isModuleDisabled(SubscriptionModule.ENDPOINT_DATA_GOVERNANCE),
      fetchSettings: async () => {
        return await privacySensitiveDataStore.getSettings();
      },
    }
  );

  const buildActionItem = (
    action: DeviceAction,
    details: DeviceDetails
  ): DevicePreviewWorkspaceActionItem => ({
    context: AppContext.SINGLE_WORKSPACE,
    action,
    enrollmentCode: details.enrollmentCode,
    selection: {
      isAllPages: false,
      include: [details],
      exclude: [],
    },
  });

  const getDeviceDetails = async (device: DeviceListItem) => {
    await devicesStore.getDeviceDetails(device.enrollmentCode);
  };

  const getDeviceActions = async (device: DeviceListItem) => {
    try {
      const payload = {
        selection: {
          isAllPages: false,
          include: [device],
          exclude: [],
        },
        target: ActionTypeTarget.SINGLE,
      };
      const { data } = await devicesStore.getActions(payload);
      return data.items;
    } catch {
      return [];
    }
  };

  const applyDeviceAction = async (
    payload: DevicePreviewActionPayload<DevicePreviewWorkspaceActionItem>
  ) => {
    await devicesStore.applyDeviceAction({
      action: payload.action,
      item: {
        action: payload.item.action,
        selection: payload.item.selection,
        partialScan: payload.item.partialScan,
        metadata: payload.item.metadata,
      },
    });
  };

  const handleAction = createActionHandler(baseConfig, applyDeviceAction, buildActionItem, {
    ticketsRoute: RouteName.TICKETS,
    remoteShellComponent: RemoteShellModal,
  });

  return {
    ...baseConfig,
    deviceDetails,
    context: AppContext.SINGLE_WORKSPACE,
    buildActionItem,
    getDeviceDetails,
    getDeviceActions,
    applyDeviceAction,
    handleAction,
    deviceSettingsDisabled,
    fetchDeviceSettings,
  };
};

// --- MSP Factory ---
export const getMspDevicePreviewConfig = (): MspDevicePreviewConfig => {
  const devicesStore = useMspDevicesStore();
  const { deviceDetails } = storeToRefs(devicesStore);
  const permissions = createPermissionRefs(AppContext.MSP);
  const baseConfig = createBaseConfig(devicesStore, permissions, AppContext.MSP);
  const { deviceSettingsDisabled, fetchDeviceSettings } =
    createDlpSettingsHandlers<MspDeviceListItem>({
      shouldSkipFetch: (device) => !device.workspaceId,
      fetchSettings: async (device) => {
        const { data } = await devicesStore.getMonitoringSettingsByWorkspace(device.workspaceId);
        return data;
      },
    });

  const buildActionItem = (
    action: DeviceAction,
    details: MspDeviceDetails
  ): DevicePreviewMspActionItem => ({
    context: AppContext.MSP,
    action,
    enrollmentCode: details.enrollmentCode,
    enrollmentCodes: [details.enrollmentCode],
  });

  const getDeviceDetails = async (device: MspDeviceListItem) => {
    await devicesStore.getDeviceDetails(device.enrollmentCode, device.workspaceId);
  };

  const getDeviceActions = async (device: MspDeviceListItem) => {
    try {
      const { data } = await devicesStore.getActions({
        enrollmentCodes: [device.enrollmentCode],
        target: ActionTypeTarget.SINGLE,
      });
      return data.items;
    } catch {
      return [];
    }
  };

  const applyDeviceAction = async (
    payload: DevicePreviewActionPayload<DevicePreviewMspActionItem>
  ) => {
    await devicesStore.applyDeviceAction({
      action: payload.action,
      item: {
        action: payload.item.action,
        enrollmentCodes: payload.item.enrollmentCodes,
        partialScan: payload.item.partialScan,
        metadata: payload.item.metadata,
      },
    });
  };

  const handleAction = createActionHandler(baseConfig, applyDeviceAction, buildActionItem, {
    ticketsRoute: RouteName.MSP_TICKETS_PAGE,
    remoteShellComponent: MspRemoteShellModal,
    excludedActions: new Set([DeviceAction.UNINSTALL_AGENT, DeviceAction.RESET_MFA]),
  });

  return {
    ...baseConfig,
    deviceDetails,
    context: AppContext.MSP,
    buildActionItem,
    getDeviceDetails,
    getDeviceActions,
    applyDeviceAction,
    handleAction,
    deviceSettingsDisabled,
    fetchDeviceSettings,
  };
};
